import { store } from "../store.js";
import { assistantChange } from "../reducers/chatReducer.js";
export class ChatSubscriptionStore {
    chats = new Map();
    seenMessageIds = new Set();
    // Listeners for the chat list (useChatListStore)
    chatsListeners = new Set();
    cachedChatsSnapshot = null;
    // Per-chat listeners (useChatStore)
    chatListeners = new Map();
    // Connection state listeners (useConnectionState)
    connectionState = "connecting";
    connectionStateListeners = new Set();
    // Initialized listeners (useChatStoreConnected) — flips true once the
    // "initial" event has populated the store.
    initialized = false;
    initializedListeners = new Set();
    setInitialChats(chats) {
        this.chats.clear();
        this.seenMessageIds.clear();
        for (const chat of chats) {
            this.chats.set(chat.id, {
                id: chat.id,
                summary: chat.summary,
                status: chat.status,
                starred: chat.starred,
                createdAt: chat.createdAt,
                lastMessageAt: chat.lastMessageAt,
                messages: chat.messages,
            });
        }
        this.invalidateInitialized(true);
        this.invalidateChats();
        for (const chatId of this.chatListeners.keys()) {
            this.invalidateChat(chatId);
        }
    }
    invalidateInitialized(initialized) {
        this.initialized = initialized;
        for (const listener of this.initializedListeners)
            listener();
    }
    handleEvent(event) {
        switch (event.type) {
            case "initial": {
                this.setInitialChats(event.chats);
                return;
            }
            case "status": {
                const chat = this.chats.get(event.chatId);
                if (chat) {
                    this.chats.set(event.chatId, { ...chat, status: event.status });
                    this.invalidateChats();
                    this.invalidateChat(event.chatId);
                }
                return;
            }
            case "summary": {
                const chat = this.chats.get(event.chatId);
                if (chat) {
                    this.chats.set(event.chatId, { ...chat, summary: event.summary });
                    this.invalidateChats();
                    this.invalidateChat(event.chatId);
                }
                return;
            }
            case "chatCreated": {
                const chat = this.chats.get(event.chatId);
                if (!chat) {
                    this.chats.set(event.chatId, event.chat);
                    this.invalidateChats();
                }
                return;
            }
            case "chatDeleted": {
                const chat = this.chats.get(event.chatId);
                if (chat) {
                    this.chats.delete(event.chatId);
                    this.invalidateChats();
                    this.invalidateChat(event.chatId);
                }
                return;
            }
            case "chatRenamed": {
                const chat = this.chats.get(event.chatId);
                if (chat) {
                    this.chats.set(event.chatId, { ...chat, summary: event.summary });
                    this.invalidateChats();
                    this.invalidateChat(event.chatId);
                }
                return;
            }
            case "chatStarred": {
                const chat = this.chats.get(event.chatId);
                if (chat) {
                    this.chats.set(event.chatId, { ...chat, starred: event.starred });
                    this.invalidateChats();
                    this.invalidateChat(event.chatId);
                }
                return;
            }
            case "message": {
                const { message, messageId, chatId, } = event;
                if (this.seenMessageIds.has(messageId))
                    return;
                this.seenMessageIds.add(messageId);
                const defaultChat = {
                    id: chatId,
                    summary: "New chat",
                    status: "pending",
                    starred: false,
                    createdAt: new Date().toISOString(),
                    lastMessageAt: new Date().toISOString(),
                    messages: [message],
                };
                const chat = this.chats.getOrInsert(chatId, defaultChat);
                const messages = chat.messages.filter(m => m.id !== 0);
                this.chats.set(chatId, {
                    ...chat,
                    messages: [...messages, message],
                    lastMessageAt: new Date().toISOString(),
                });
                this.invalidateChat(chatId);
                if (message.role === "assistant" && "spec" in message && message.spec) {
                    const text = message.content.find(b => b.type === "text");
                    store.dispatch(assistantChange({
                        chatId,
                        entry: {
                            messageId,
                            text: text?.text ?? "Assistant change",
                            spec: message.spec,
                        },
                    }));
                }
                return;
            }
        }
    }
    addOptimisticMessage(chatId, message) {
        const chat = this.chats.get(chatId);
        if (chat) {
            this.chats.set(chatId, {
                ...chat,
                messages: [...chat.messages, message],
            });
            this.invalidateChat(chatId);
        }
    }
    addSemiOptimisticChat = (chat) => {
        this.chats.set(chat.id, chat);
        this.invalidateChats();
    };
    getChat = (chatId) => {
        return this.chats.get(chatId);
    };
    /**
     * Subscribe to chat list changes (create, delete, initial, summary).
     * Used by useChatListStore.
     */
    subscribeChats = (listener) => {
        this.chatsListeners.add(listener);
        return () => {
            this.chatsListeners.delete(listener);
        };
    };
    getChatsSnapshot = () => {
        this.cachedChatsSnapshot ??= Array.from(this.chats.values());
        return this.cachedChatsSnapshot;
    };
    /**
     * Subscribe to a specific chat's changes (message, status, summary).
     * Used by useChatStore.
     */
    subscribeChat = (chatId, listener) => {
        const listeners = this.chatListeners.getOrInsert(chatId, new Set());
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
            if (listeners.size === 0) {
                this.chatListeners.delete(chatId);
            }
        };
    };
    getChatSnapshot = (chatId) => {
        return this.chats.get(chatId);
    };
    setConnectionState(state) {
        if (this.connectionState === state)
            return;
        this.connectionState = state;
        for (const listener of this.connectionStateListeners)
            listener();
    }
    subscribeConnectionState = (listener) => {
        this.connectionStateListeners.add(listener);
        return () => {
            this.connectionStateListeners.delete(listener);
        };
    };
    getConnectionStateSnapshot = () => {
        return this.connectionState;
    };
    subscribeInitialized = (listener) => {
        this.initializedListeners.add(listener);
        return () => {
            this.initializedListeners.delete(listener);
        };
    };
    getInitializedSnapshot = () => {
        return this.initialized;
    };
    invalidateChats() {
        this.cachedChatsSnapshot = null;
        for (const listener of this.chatsListeners) {
            listener();
        }
    }
    invalidateChat(chatId) {
        const listeners = this.chatListeners.get(chatId);
        if (listeners) {
            for (const listener of listeners) {
                listener();
            }
        }
    }
    clear() {
        this.chats.clear();
        this.seenMessageIds.clear();
        this.connectionState = "connecting";
        this.invalidateInitialized(false);
    }
}
const stores = new Map();
export function getChatSubscriptionStore(styleId) {
    return stores.getOrInsertComputed(styleId, () => new ChatSubscriptionStore());
}
